<template>
  <v-container v-if="!loading && !lobby" class="text-center">
    <p class="text-h6">{{ $t('trivia.lobby.error.load') }}</p>
    <v-btn color="primary" @click="loadLobby" class="mt-4">{{ $t('trivia.lobby.button.retry') }}</v-btn>
  </v-container>

  <v-container v-else-if="lobby" class="lobby-container" fluid>
    <v-row justify="center" class="mb-6">
      <v-col cols="12" md="10" lg="8" class="text-center">
        <h1 class="trivia-title">{{ triviaTitle }}</h1>
        <p class="subtitle-1">{{ $t('trivia.lobby.waiting') }}</p>

        <v-row justify="center" class="tw-lobby-badges">
          <v-col cols="12" sm="6" class="text-center">
            <div class="tw-badge tw-badge--pin">
              <div class="tw-badge__label">{{ $t('trivia.lobby.pinLabel') }}</div>
              <div class="tw-badge__value">{{ lobby.pin }}</div>
              <v-btn small text color="primary" @click="copyPin">
                <v-icon left small>mdi-content-copy</v-icon>
                {{ $t('trivia.lobby.button.copy') }}
              </v-btn>
            </div>
          </v-col>

          <v-col cols="12" sm="6" class="text-center">
            <div class="tw-badge tw-badge--counter">
              <v-icon color="success" size="36" class="tw-badge__icon">mdi-account-group</v-icon>
              <div class="tw-badge__value">{{ participants.length }}</div>
              <div class="tw-badge__label">{{ $t('trivia.lobby.participantsLabel') }}</div>
            </div>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row justify="center" class="mb-6">
      <v-col cols="12" md="10" lg="8">
        <v-card class="tw-panel" raised>
          <h3 class="tw-panel__title text-center">{{ $t('trivia.lobby.section.participants') }}</h3>
          <div v-if="participants.length > 0" class="participants-grid d-flex flex-wrap justify-center">
            <div
              v-for="(p, i) in participants"
              :key="p.userId || i"
              class="participant-chip text-center"
            >
              <v-avatar class="participant-avatar mb-2" :color="getParticipantColor(i)" size="48">
                <span class="white--text text-h6">{{ getParticipantInitials(p) }}</span>
              </v-avatar>
              <div class="participant-name font-weight-bold">
                {{ p.fullName || p.name || p.nickname || $t('trivia.lobby.participant.fallback', { number: i + 1 }) }}
              </div>
            </div>
          </div>
          <p v-else class="text-center text--secondary my-4">{{ $t('trivia.lobby.empty') }}</p>
        </v-card>
      </v-col>
    </v-row>

    <v-row justify="center">
      <v-col cols="12" md="8" lg="6" class="d-flex justify-space-between">
        <v-btn color="error" outlined large @click="cancelSession" :loading="processing">
          {{ $t('trivia.lobby.button.cancel') }}
        </v-btn>
        <v-btn color="success" large @click="startSession" :loading="processing" :disabled="participants.length === 0">
          {{ $t('trivia.lobby.button.start') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { service as TriviaService } from '@/services/trivia_service.js';
import Swal from "sweetalert2";

function getSessionContext() {
  return {
    idGroup: sessionStorage.getItem('idGroup'),
    idEmpresa: sessionStorage.getItem('idEmpresa'),
    idUsuario: sessionStorage.getItem('idUsuario')
  };
}

export default {
  name: 'TriviaSessionLobbyComponent',

  data() {
    return {
      sessionId: null,
      triviaTitle: '',
      lobby: null,
      participants: [],
      loading: true,
      processing: false,
      pollInterval: null
    };
  },

  created() {
    this.sessionId = this.$route.params.sessionId;
    this.triviaTitle = this.$route.query.triviaTitle || this.$t('trivia.lobby.title.fallback');
    this.hydrateFromCache();
    this.loadLobby();
    this.startPolling();
  },

  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    hydrateFromCache() {
      try {
        const cached = sessionStorage.getItem('trivia_session_' + this.sessionId);
        if (cached) {
          this.applyPayload(JSON.parse(cached));
        }
      } catch (e) {
        console.warn('No se pudo leer la sesion cacheada', e);
      }
    },

    applyPayload(payload) {
      if (!payload) return;

      this.lobby = Object.assign({}, this.lobby || {}, payload);

      const list = payload.participants || payload.players || payload.participantes;
      if (Array.isArray(list)) {
        this.participants = list;
      }

      if (payload.triviaTitle) {
        this.triviaTitle = payload.triviaTitle;
      }
    },

    async loadLobby() {
      const { idGroup, idEmpresa, idUsuario } = getSessionContext();
      this.loading = true;
      try {
        let payload = null;

        try {
          const resp = await TriviaService.getSessionLobby(idGroup, idEmpresa, idUsuario, this.sessionId);
          payload = resp.data && resp.data.payload;
        } catch (lobbyErr) {
          console.warn('Endpoint /lobby no disponible, usando detalle de sesion', lobbyErr);
          const resp = await TriviaService.getSessionById(idGroup, idEmpresa, idUsuario, this.sessionId);
          payload = resp.data && resp.data.payload;
        }

        this.applyPayload(payload);

        const status = this.lobby && this.lobby.status;
        if (status && status !== 'LOBBY' && status !== 'CREATED') {
          this.stopPolling();
          this.$router.replace({ name: 'trivia-live', params: { sessionId: this.sessionId }, query: this.$route.query });
        }
      } catch (err) {
        console.error('Error cargando lobby', err);
      } finally {
        this.loading = false;
      }
    },

    startPolling() {
      this.pollInterval = setInterval(this.loadLobby, 2000);
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },

    getParticipantColor(index) {
      const colors = ['primary', 'success', 'warning', 'error', 'info', 'purple', 'pink', 'indigo', 'teal', 'orange'];
      return colors[index % colors.length];
    },

    getParticipantInitials(p) {
      const text = String(p.fullName || p.name || p.nickname || '').trim();
      const parts = text.split(/\s+/).filter(Boolean);
      if (parts.length === 0) return '?';
      if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },

    async copyPin() {
      if (!this.lobby || !this.lobby.pin) return;
      const text = String(this.lobby.pin);
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.style.position = 'fixed';
          textarea.style.left = '-9999px';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }
        this.$toasted && this.$toasted.show && this.$toasted.show(this.$t('trivia.lobby.toast.pinCopied'));
      } catch (e) {
        console.error('Error copiando PIN', e);
      }
    },

    async startSession() {
      const { idGroup, idEmpresa, idUsuario } = getSessionContext();
      this.processing = true;
      try {
        await TriviaService.postStartSession(idGroup, idEmpresa, idUsuario, this.sessionId);
        this.stopPolling();
        this.$router.push({ name: 'trivia-live', params: { sessionId: this.sessionId }, query: this.$route.query });
      } catch (err) {
        console.error('Error iniciando sesión', err);
        Swal.fire(this.$t('trivia.common.no'), this.$t('trivia.lobby.error.start'), 'error');
      } finally {
        this.processing = false;
      }
    },

    async cancelSession() {
      const { idGroup, idEmpresa, idUsuario } = getSessionContext();
      const result = await Swal.fire({
        title: this.$t('trivia.lobby.confirm.cancelTitle'),
        text: this.$t('trivia.lobby.confirm.cancelText'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('trivia.lobby.confirm.cancelAccept'),
        cancelButtonText: this.$t('trivia.common.no')
      });
      if (!result.isConfirmed) return;

      this.processing = true;
      try {
        await TriviaService.postCancelSession(idGroup, idEmpresa, idUsuario, this.sessionId);
        this.stopPolling();
        this.$router.push({ name: 'trivia' });
      } catch (err) {
        console.error('Error cancelando sesión', err);
        Swal.fire(this.$t('trivia.common.no'), this.$t('trivia.lobby.error.cancel'), 'error');
      } finally {
        this.processing = false;
      }
    }
  }
};
</script>

<style lang="css" scoped>
.lobby-container {
  min-height: 100vh;
  padding-top: 32px;
}

.trivia-title {
  color: #fff;
  font-weight: 700;
  font-size: 2.5rem;
}

.pin-box {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 16px;
  padding: 24px 48px;
  display: inline-block;
}

.pin-label {
  color: #fff;
  font-size: 1rem;
  letter-spacing: 4px;
  text-transform: uppercase;
}

.pin-number {
  color: #fff;
  font-size: 5rem;
  font-weight: 900;
  letter-spacing: 12px;
}

.participant-counter {
  display: inline-flex;
  align-items: center;
}

.participants-grid {
  gap: 12px;
  padding: 16px;
}

.participant-chip {
  background: #f3f4f6;
  border-radius: 16px;
  padding: 12px 16px;
  min-width: 120px;
  max-width: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: transform 0.2s ease, background 0.2s ease;
}

.participant-chip:hover {
  transform: translateY(-2px);
  background: #e5e7eb;
}

.participant-name {
  font-size: 0.95rem;
  color: #1f2937;
  max-width: 100%;
  word-break: break-word;
  line-height: 1.2;
  min-height: 1.2em;
}
</style>
